import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import StatusBadge from "@/components/StatusBadge";
import OnboardingGuide from "@/components/OnboardingGuide";
import { Activity, FileWarning, FileCheck2, FileX2, ShieldAlert, ListChecks, Clock, Gauge } from "lucide-react";

const KPIS = [
  { key: "operationsEnCours", label: "Opérations en cours", icon: Activity },
  { key: "pdpAValider", label: "Plans de prévention à valider", icon: FileWarning, color: "var(--ambre-f)" },
  { key: "permisActifs", label: "Permis actifs", icon: FileCheck2, color: "var(--vert)" },
  { key: "permisExpires", label: "Permis expirés", icon: FileX2, color: "var(--rouge)" },
  { key: "incompatibilitesBloquantes", label: "Incompatibilités bloquantes", icon: ShieldAlert, color: "var(--rouge)" },
  { key: "actionsOuvertes", label: "Actions ouvertes", icon: ListChecks },
  { key: "actionsRetard", label: "Actions en retard", icon: Clock, color: "var(--ambre-f)" },
];

export default function Dashboard() {
  const { selectedSite, sites, entreprises } = useApp();
  const [d, setD] = useState(null);
  const [opsCount, setOpsCount] = useState(null);
  const [inclFormation, setInclFormation] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const params = [];
    if (selectedSite !== "all") params.push(`siteId=${selectedSite}`);
    if (inclFormation) params.push("includeFormation=true");
    const q = params.length ? `?${params.join("&")}` : "";
    api.get(`/dashboard${q}`).then((r) => setD(r.data));
  }, [selectedSite, inclFormation]);

  useEffect(() => {
    api.get("/operations").then((r) => setOpsCount(r.data.length)).catch(() => setOpsCount(0));
  }, []);

  if (!d || opsCount === null) return <div className="mono">Chargement…</div>;

  const needsOnboarding = sites.length === 0 || entreprises.length === 0 || opsCount === 0;

  return (
    <div>
      <div className="crumb">Vue de pilotage · {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <h1>Tableau de bord</h1>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: ".82rem", cursor: "pointer" }} data-testid="dash-toggle-formation">
          <input type="checkbox" checked={inclFormation} onChange={(e) => setInclFormation(e.target.checked)} />
          Inclure les opérations de formation
        </label>
      </div>

      {needsOnboarding && <OnboardingGuide hasOperations={opsCount > 0} />}

      <div className="grid4">
        {KPIS.slice(0, 4).map((k) => (
          <div className="kpi" key={k.key} data-testid={`kpi-${k.key}`}>
            <k.icon className="ic" size={34} />
            <div className="n" style={{ color: d[k.key] ? (k.color || "var(--text-main)") : "var(--text-main)" }}>{d[k.key]}</div>
            <div className="l">{k.label}</div>
          </div>
        ))}
      </div>
      <div className="grid4">
        {KPIS.slice(4).map((k) => (
          <div className="kpi" key={k.key} data-testid={`kpi-${k.key}`}>
            <k.icon className="ic" size={34} />
            <div className="n" style={{ color: d[k.key] ? (k.color || "var(--text-main)") : "var(--text-main)" }}>{d[k.key]}</div>
            <div className="l">{k.label}</div>
          </div>
        ))}
        <div className="kpi" data-testid="kpi-criticite">
          <Gauge className="ic" size={34} />
          <div style={{ marginTop: 6 }}><StatusBadge value={d.criticiteGlobale} label={d.criticiteGlobaleLabel} /></div>
          <div className="l" style={{ marginTop: 8 }}>Niveau de criticité global</div>
        </div>
      </div>

      <h2 style={{ fontSize: "1.1rem", margin: "18px 0 10px" }}>Conflits de coactivité du jour</h2>
      {d.conflitsDuJour.length ? d.conflitsDuJour.map((c) => (
        <div className={`conf ${c.niv === "warn" ? "warn" : ""}`} key={c.key} data-testid={`conflict-${c.code}`}>
          <div className="t"><span className="mono">{c.code}</span> · {c.a} × {c.b}</div>
          <div className="d">{c.msg}</div>
          <div className="fix">→ {c.fix || "Coordination à tracer au plan de prévention."}</div>
        </div>
      )) : <div className="card" style={{ borderLeft: "4px solid var(--vert)" }}>Aucun conflit de coactivité bloquant détecté aujourd'hui.</div>}

      <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn btn-p" onClick={() => nav("/operations/new")} data-testid="dash-new-op">+ Nouvelle opération</button>
        <button className="btn btn-g" onClick={() => nav("/coactivite")}>Analyse coactivité</button>
        <button className="btn btn-g" onClick={() => nav("/actions")}>Plan d'actions</button>
      </div>
    </div>
  );
}
